import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { CompanyType } from '@planning/app/api'
import { CompanyAutocomplete } from '@planning/components/autocompletes/company-autocomplete/CompanyAutocomplete'
import {
  formatRailcarName,
  isStandardRailcarName,
  unformatRailcarName,
} from '@planning/utils/railcar-utils'
import { useTranslate } from '@tolgee/react'
import { BinIcon, CustomInputAdornment, useMinimalsTheme } from '@tom-ui/ui'
import { MAX_INT32_VALUE, notAllowPlusMinusPointSign } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { RailcarsViewStore } from '../stores/RailcarsViewStore'

interface Props {
  store: RailcarsViewStore
}

export interface RailcarFormData {
  id?: number
  operatorId?: number
  railcarNumber: string
  railcarLength: number | string
  maxWeight?: number | string
  maxTEU?: number | string
}

const InvalidRailcarNumberInfoMessage = () => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  const invalidRailcarNumberText = t(
    'invalidRailcarNumberSuggestion',
    'You are about to add a railcar with a non-standard format. The recommended format is: 1234 567 8901-1',
  )

  return (
    <Box>
      <Stack
        gap={theme.customSpacing.xxs}
        direction={'row'}
        alignItems={'center'}
        sx={{
          backgroundColor: theme.palette.warning.lighter,
          color: theme.palette.warning.dark,
          padding: `${theme.customSpacing.xs}`,
          borderRadius: theme.customRadius.small,
          margin: `${theme.customSpacing.xs} 0`,
        }}
      >
        <Typography variant='caption'>{invalidRailcarNumberText}</Typography>
      </Stack>
    </Box>
  )
}

export const RailcarsFormDialog = observer(({ store }: Props) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const title = store.dataToBeEdited
    ? t('editRailcar', 'Edit railcar')
    : t('addNewRailcar', 'Add new railcar')

  const {
    control,
    reset,
    formState: { errors },
    getValues,
    trigger,
    watch,
  } = useForm<RailcarFormData>({
    defaultValues: {
      railcarNumber: '',
      railcarLength: '',
      operatorId: -1,
      maxWeight: '',
      maxTEU: '',
    },
  })

  useEffect(() => {
    if (store.dataToBeEdited) {
      reset({
        railcarNumber: formatRailcarName(store.dataToBeEdited?.name),
        railcarLength: store.dataToBeEdited?.length ?? '',
        operatorId: store.dataToBeEdited?.operatorId ?? undefined,
        id: store.dataToBeEdited.id,
        maxWeight: store.dataToBeEdited.maxWeight ?? '',
        maxTEU: store.dataToBeEdited?.maxTEU ?? '',
      })
    } else {
      reset({
        railcarNumber: formatRailcarName(store.defaultRailcarNumber ?? ''),
        railcarLength: '',
        maxWeight: '',
        maxTEU: '',
      })
    }
  }, [store.dataToBeEdited, store.defaultRailcarNumber, reset, store.isDialogOpen])

  const onSubmit: SubmitHandler<RailcarFormData> = async data => {
    await store.onSubmit(data)

    if (!store.isDialogOpen) reset()
  }

  const handleDialogClose = () => {
    store.toggleDialog(false)
  }

  const handleDelete = () => {
    if (store.dataToBeEdited) {
      store.onDelete(store.dataToBeEdited)
    }
  }

  /**
   * We use a custom submit handler instead of a default submit button
   * to control form submission explicitly. This change avoids automatic
   * form submission for better reuse of the component.
   */
  const handleFormSubmit = async () => {
    const isValid = await trigger()
    if (isValid) {
      const formData = getValues()
      formData.railcarNumber = unformatRailcarName(formData.railcarNumber)

      onSubmit(formData)
    }
  }

  const handleNumberChange = (number: string) => {
    if (number === '') {
      return ''
    }

    const intValue = Number.parseInt(number)

    if (!Number.isNaN(intValue)) {
      if (intValue <= MAX_INT32_VALUE) {
        return number
      } else {
        return MAX_INT32_VALUE.toString()
      }
    }
  }

  const railcarNumber = watch('railcarNumber')?.trim() ?? ''
  const showInvalidRailcarNumberInfoMessage =
    railcarNumber.length > 0 && !isStandardRailcarName(railcarNumber)

  return (
    <Dialog fullWidth maxWidth='md' open={store.isDialogOpen} onClose={handleDialogClose}>
      <DialogTitle>{title}</DialogTitle>
      <form>
        <DialogContent>
          <Grid container spacing={1} paddingTop={0.2}>
            <Grid size={12}>
              <Controller
                name='railcarNumber'
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('railcarNumber', 'Railcar Number')}
                    variant='outlined'
                    fullWidth
                    required
                    slotProps={{
                      htmlInput: {
                        maxLength: 15,
                      },
                    }}
                    onChange={e => {
                      const rawValue = formatRailcarName(e.target.value)
                      const formattedValue = formatRailcarName(rawValue)

                      field.onChange(formattedValue)
                    }}
                    error={!!errors.railcarNumber}
                    helperText={
                      errors.railcarNumber ? t('fieldIsRequired', 'Field is required.') : ''
                    }
                  />
                )}
              />
              {showInvalidRailcarNumberInfoMessage && <InvalidRailcarNumberInfoMessage />}
            </Grid>
            <Grid size={12}>
              <Controller
                name='operatorId'
                control={control}
                render={({ field }) => (
                  <CompanyAutocomplete
                    label={t('operator', 'Operator')}
                    filterByTypes={[CompanyType.TrainOperator]}
                    selectedCompanyId={field.value}
                    handleOnChange={operator => {
                      field.onChange(operator?.id)
                    }}
                    dataCy='operator-autocomplete'
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <Controller
                name='railcarLength'
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('railcarLength', 'Railcar Length')}
                    variant='outlined'
                    fullWidth
                    type='number'
                    slotProps={{
                      input: {
                        endAdornment: (
                          <CustomInputAdornment position='end' color='secondary'>
                            m
                          </CustomInputAdornment>
                        ),
                      },
                      htmlInput: {
                        min: '0',
                        step: '0.01',
                        pattern: '^\\d*(\\.\\d{0,2})?$',
                      },
                    }}
                    onChange={e => {
                      const value = e.target.value
                      if (/^\d*(\.\d{0,2})?$/.test(value) || value === '') {
                        const correctNumber = handleNumberChange(value)
                        field.onChange(correctNumber)
                      }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <Controller
                name='maxWeight'
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('maxWeight', 'Max weight')}
                    variant='outlined'
                    fullWidth
                    type='number'
                    slotProps={{
                      input: {
                        endAdornment: (
                          <CustomInputAdornment position='end' color='secondary'>
                            kg
                          </CustomInputAdornment>
                        ),
                      },
                      htmlInput: {
                        min: '0',
                        step: '1',
                        pattern: '[0-9]*',
                      },
                    }}
                    onKeyDown={notAllowPlusMinusPointSign}
                    onChange={e => {
                      const value = e.target.value.replace(/\D/g, '')
                      const correctNumber = handleNumberChange(value)

                      field.onChange(correctNumber)
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <Controller
                name='maxTEU'
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('maxTEU', 'Max TEU')}
                    variant='outlined'
                    fullWidth
                    type='number'
                    slotProps={{
                      htmlInput: {
                        min: '0',
                        step: '1',
                        pattern: '[0-9]*',
                      },
                    }}
                    onKeyDown={notAllowPlusMinusPointSign}
                    onChange={e => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 1)
                      field.onChange(value)
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            mt: theme.customSpacing.l,
            display: 'flex',
            justifyContent: 'space-between',
            borderTop: `1px solid ${theme.palette.grey[300]}`,
          }}
        >
          <Stack direction={'row'} spacing={2}>
            <Button onClick={handleFormSubmit} variant='contained' color='primary'>
              {t('save', 'Save')}
            </Button>
            <Button onClick={handleDialogClose} color='secondary'>
              {t('cancel', 'Cancel')}
            </Button>
          </Stack>
          {store.dataToBeEdited && (
            <IconButton onClick={handleDelete} color='secondary'>
              <BinIcon />
            </IconButton>
          )}
        </DialogActions>
      </form>
    </Dialog>
  )
})
